import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { automaticInvoicesApiService } from '../api/automatic-invoices-api';
import {
  AutomaticInvoice,
  AutomaticInvoiceFilters,
  AutomaticInvoiceStats,
  AutomaticInvoicesState,
} from '../types/admin.types';

// Función para calcular estadísticas
const calculateStats = (
  invoices: AutomaticInvoice[],
): AutomaticInvoiceStats => {
  return {
    total: invoices.length,
    active: invoices.filter((inv) => inv.isActive).length,
    inactive: invoices.filter((inv) => !inv.isActive).length,
    monthly: invoices.filter((inv) => inv.frequency === 'monthly').length,
    yearly: invoices.filter((inv) => inv.frequency === 'yearly').length,
    totalAmount: invoices.reduce((sum, inv) => sum + Number(inv.amount || 0), 0),
  };
};

// Estado inicial
const initialAutomaticInvoicesState: AutomaticInvoicesState = {
  automaticInvoices: [],
  currentAutomaticInvoice: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
  },
  filters: {
    currentMonth: true, // Por defecto, mostrar solo facturas del mes actual
  },
  stats: {
    total: 0,
    active: 0,
    inactive: 0,
    monthly: 0,
    yearly: 0,
    totalAmount: 0,
  },
};

// Async Thunks
export const fetchAutomaticInvoices = createAsyncThunk(
  'automaticInvoices/fetchAutomaticInvoices',
  async (filters: AutomaticInvoiceFilters = {}, { rejectWithValue }) => {
    try {
      const response =
        await automaticInvoicesApiService.getAllAutomaticInvoices(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          'Error al cargar facturas automáticas',
      );
    }
  },
);

export const fetchAutomaticInvoice = createAsyncThunk(
  'automaticInvoices/fetchAutomaticInvoice',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await automaticInvoicesApiService.getAutomaticInvoice(
        id,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          'Error al cargar factura automática',
      );
    }
  },
);

export const fetchAutomaticInvoiceStats = createAsyncThunk(
  'automaticInvoices/fetchAutomaticInvoiceStats',
  async (_, { rejectWithValue }) => {
    try {
      const response =
        await automaticInvoicesApiService.getAutomaticInvoiceStats();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar estadísticas',
      );
    }
  },
);

export const toggleAutomaticInvoice = createAsyncThunk(
  'automaticInvoices/toggleAutomaticInvoice',
  async (id: string, { rejectWithValue }) => {
    try {
      const response =
        await automaticInvoicesApiService.toggleAutomaticInvoice(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          'Error al cambiar estado de factura automática',
      );
    }
  },
);

export const updateDayOfMonth = createAsyncThunk(
  'automaticInvoices/updateDayOfMonth',
  async (
    { id, dayOfMonth }: { id: string; dayOfMonth: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await automaticInvoicesApiService.updateDayOfMonth(
        id,
        dayOfMonth,
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar día del mes',
      );
    }
  },
);

// Slice
const automaticInvoicesSlice = createSlice({
  name: 'automaticInvoices',
  initialState: initialAutomaticInvoicesState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentAutomaticInvoice: (
      state,
      action: PayloadAction<AutomaticInvoice | null>,
    ) => {
      state.currentAutomaticInvoice = action.payload;
    },
    setFilters: (state, action: PayloadAction<AutomaticInvoiceFilters>) => {
      state.filters = action.payload;
    },
    setPagination: (
      state,
      action: PayloadAction<{ page: number; limit: number; total: number }>,
    ) => {
      state.pagination = action.payload;
    },
    clearAutomaticInvoices: (state) => {
      state.automaticInvoices = [];
      state.currentAutomaticInvoice = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchAutomaticInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAutomaticInvoices.fulfilled, (state, action) => {
        state.loading = false;
        const invoicesData =
          (action.payload as any)?.data || action.payload;
        state.automaticInvoices = Array.isArray(invoicesData)
          ? invoicesData
          : [];
        state.pagination.total = state.automaticInvoices.length;
        state.stats = calculateStats(state.automaticInvoices);
      })
      .addCase(fetchAutomaticInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch one
      .addCase(fetchAutomaticInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAutomaticInvoice.fulfilled, (state, action) => {
        state.loading = false;
        const invoiceData = (action.payload as any)?.data || action.payload;
        state.currentAutomaticInvoice = invoiceData;
      })
      .addCase(fetchAutomaticInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch stats
      .addCase(fetchAutomaticInvoiceStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAutomaticInvoiceStats.fulfilled, (state, action) => {
        state.loading = false;
        const statsData = (action.payload as any)?.data || action.payload;
        state.stats = statsData;
      })
      .addCase(fetchAutomaticInvoiceStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Toggle
      .addCase(toggleAutomaticInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleAutomaticInvoice.fulfilled, (state, action) => {
        state.loading = false;
        const invoiceData = (action.payload as any)?.data || action.payload;
        const index = state.automaticInvoices.findIndex(
          (invoice) => invoice.id === invoiceData.id,
        );
        if (index !== -1) {
          state.automaticInvoices[index] = invoiceData;
        }
        if (state.currentAutomaticInvoice?.id === invoiceData.id) {
          state.currentAutomaticInvoice = invoiceData;
        }
        state.stats = calculateStats(state.automaticInvoices);
      })
      .addCase(toggleAutomaticInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update day of month
      .addCase(updateDayOfMonth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDayOfMonth.fulfilled, (state, action) => {
        state.loading = false;
        const invoiceData = (action.payload as any)?.data || action.payload;
        const index = state.automaticInvoices.findIndex(
          (invoice) => invoice.id === invoiceData.id,
        );
        if (index !== -1) {
          state.automaticInvoices[index] = invoiceData;
        }
        if (state.currentAutomaticInvoice?.id === invoiceData.id) {
          state.currentAutomaticInvoice = invoiceData;
        }
      })
      .addCase(updateDayOfMonth.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setCurrentAutomaticInvoice,
  setFilters,
  setPagination,
  clearAutomaticInvoices,
} = automaticInvoicesSlice.actions;

export default automaticInvoicesSlice.reducer;

